import React, { useMemo, useState, useEffect, useRef } from "react";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import { MonthOptions, YesAndNoOptions } from "utils/constant";
import InputDateTime from "components/InputDateTime";
import KelambuDistribution from "services/KelambuDistribution";
import moment from "moment";

const KelambuDistributionService = new KelambuDistribution();

const toInt = (v) =>
  v === "" || v === null || v === undefined ? 0 : parseInt(v, 10) || 0;

const KelambuMassalTotalFields = ({
  formData,
  handleInputChange,
  setFormData,
  memoizedValues,
  disabledNetReceived,
  inputDisabled,
  fetchPrevKelambuMassalDiterimaDesa,
  fetchTotalKelambuDesk,
}) => {
  const [rowErrors, setRowErrors] = useState({});
  const [summaryError, setSummaryError] = useState("");

  const [remainingKelambu, setRemainingKelambu] = useState(0);

  const fetchRemainingKelambuDesk = async (
    urbanvillage_id,
    net_distribution_date,
    year,
    vector_controll_type
  ) => {
    try {
      await KelambuDistributionService.remainingDesk(
        urbanvillage_id,
        net_distribution_date,
        year,
        vector_controll_type
      )
        .then((res) => {
          setRemainingKelambu(res.sisa_kelambu_tersedia_accumulated);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
    }
  };

  const [fieldErrors, setFieldErrors] = useState({
    total_net_distribution: "",
    total_nets_distributed_to_pregnant_women: "",
  });

  const clearTimers = useRef({});

  const setFieldErrorWithAutoClear = (key, msg) => {
    if (clearTimers.current[key]) {
      clearTimeout(clearTimers.current[key]);
    }
    setFieldErrors((prev) => ({ ...prev, [key]: msg }));
    clearTimers.current[key] = setTimeout(() => {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }, 3000);
  };

  const isDusun =
    formData.kelambu_massal_total.is_dusun_level_distribution === "Ya";

  const totalDusun = useMemo(() => {
    return (formData.kelambu_massal_total.sub_villages_data || []).reduce(
      (s, v) => s + toInt(v && v.nets_distributed),
      0
    );
  }, [formData.kelambu_massal_total.sub_villages_data]);

  const totalReceived = toInt(
    formData.kelambu_massal_total.total_mosquito_nets_received
  );
  const totalDistribVillage = toInt(
    formData.kelambu_massal_total.total_net_distribution
  );
  const totalPregnant = toInt(
    formData.kelambu_massal_total.total_nets_distributed_to_pregnant_women
  );

  useEffect(() => {
    if (!isDusun) return;
    if (totalDistribVillage !== totalDusun) {
      setFormData((prev) => ({
        ...prev,
        kelambu_massal_total: {
          ...prev.kelambu_massal_total,
          total_net_distribution: totalDusun,
        },
      }));
    }
  }, [isDusun, totalDusun]);

  useEffect(() => {
    if (totalDusun > totalReceived) {
      setSummaryError(
        `Total dusun (${totalDusun.toLocaleString()}) melebihi kelambu diterima desa (${totalReceived.toLocaleString()}).`
      );
    } else if (totalDistribVillage > 0 && totalDusun > totalDistribVillage) {
      setSummaryError(
        `Total dusun (${totalDusun.toLocaleString()}) melebihi total kelambu terdistribusi desa (${totalDistribVillage.toLocaleString()}).`
      );
    } else {
      setSummaryError("");
    }
  }, [totalDusun, totalReceived, totalDistribVillage]);

  const clampAndForward = (e) => {
    const { name } = e.target;
    const raw = toInt(e.target.value);

    if (totalReceived <= 0) return handleInputChange(e, "kelambu_massal_total");

    if (name === "total_net_distribution" && !isDusun) {
      const capped = Math.min(raw, totalReceived);
      if (capped !== raw) {
        setFormData((prev) => ({
          ...prev,
          kelambu_massal_total: {
            ...prev.kelambu_massal_total,
            total_net_distribution: capped,
          },
        }));
        setFieldErrorWithAutoClear(
          "total_net_distribution",
          `Nilai dibatasi ke ${capped.toLocaleString()} karena tidak boleh melebihi jumlah kelambu yang diterima desa (${totalReceived.toLocaleString()}).`
        );
        return;
      }
    }

    if (name === "total_nets_distributed_to_pregnant_women") {
      let limit = totalReceived;
      let errorMsg = `Nilai dibatasi ke {val} karena tidak boleh melebihi jumlah kelambu yang diterima desa (${totalReceived.toLocaleString()}).`;

      if (totalDistribVillage > 0 && totalDistribVillage < totalReceived) {
        limit = totalDistribVillage;
        errorMsg = `Nilai dibatasi ke {val} karena tidak boleh melebihi jumlah kelambu massal terdistribusi (${totalDistribVillage.toLocaleString()}).`;
      }

      const capped = Math.min(raw, limit);

      if (capped !== raw) {
        setFormData((prev) => ({
          ...prev,
          kelambu_massal_total: {
            ...prev.kelambu_massal_total,
            total_nets_distributed_to_pregnant_women: capped,
          },
        }));
        setFieldErrorWithAutoClear(
          "total_nets_distributed_to_pregnant_women",
          errorMsg.replace("{val}", capped.toLocaleString())
        );
        return;
      }
    }

    handleInputChange(e, "kelambu_massal_total");
  };

  useEffect(() => {
    if (totalReceived <= 0) return;

    const next = {};
    let needUpdate = false;

    if (!isDusun && totalDistribVillage > totalReceived) {
      next.total_net_distribution = Math.min(
        totalDistribVillage,
        totalReceived
      );
      needUpdate = true;
      setFieldErrorWithAutoClear(
        "total_net_distribution",
        `Nilai dibatasi ke ${next.total_net_distribution.toLocaleString()} karena tidak boleh melebihi jumlah kelambu yang diterima desa.`
      );
    }

    let limitPregnant = totalReceived;
    let limitReason = "jumlah kelambu yang diterima desa";

    if (totalDistribVillage > 0 && totalDistribVillage < totalReceived) {
      limitPregnant = totalDistribVillage;
      limitReason = "jumlah kelambu massal terdistribusi";
    }

    if (totalPregnant > limitPregnant) {
      // reset ke 0
      next.total_nets_distributed_to_pregnant_women = 0;
      needUpdate = true;
      setFieldErrorWithAutoClear(
        "total_nets_distributed_to_pregnant_women",
        `Nilai di reset ke 0 karena tidak boleh melebihi ${limitReason}.`
      );
    }

    if (needUpdate) {
      setFormData((prev) => ({
        ...prev,
        kelambu_massal_total: {
          ...prev.kelambu_massal_total,
          ...next,
        },
      }));
    }
  }, [totalReceived, totalDistribVillage, totalPregnant, isDusun]);

  const handleChangeNetsDistributed = (e, index) => {
    const incoming = toInt(e.target.value);

    const list = [...(formData.kelambu_massal_total.sub_villages_data || [])];

    const othersSum = list.reduce(
      (sum, v, idx) =>
        idx === index ? sum : sum + toInt(v && v.nets_distributed),
      0
    );

    const capByReceived = Math.max(totalReceived - othersSum, 0);

    const isDusunLocal =
      formData.kelambu_massal_total.is_dusun_level_distribution === "Ya";

    let hardMax = capByReceived;
    let violatedMsg = "";

    if (!isDusunLocal) {
      const capByDistrib =
        totalDistribVillage > 0
          ? Math.max(totalDistribVillage - othersSum, 0)
          : Infinity;
      hardMax = Math.min(capByReceived, capByDistrib);

      if (incoming > hardMax) {
        violatedMsg =
          capByDistrib < capByReceived
            ? "Tidak boleh melebihi jumlah kelambu terdistribusi tingkat desa"
            : "Tidak boleh melebihi jumlah kelambu yang diterima desa";
      }
    } else {
      if (incoming > hardMax) {
        violatedMsg = "Tidak boleh melebihi jumlah kelambu yang diterima desa";
      }
    }

    const clamped = Math.min(incoming, hardMax);

    setRowErrors((prev) => ({
      ...prev,
      [index]: violatedMsg && incoming > clamped ? violatedMsg : "",
    }));

    list[index] = {
      ...list[index],
      nets_distributed: clamped,
    };

    const newTotalDusun = othersSum + clamped;

    setFormData((prev) => ({
      ...prev,
      kelambu_massal_total: {
        ...prev.kelambu_massal_total,
        sub_villages_data: list,
        ...(isDusunLocal ? { total_net_distribution: newTotalDusun } : {}),
      },
    }));
  };

  return (
    <div className="kelambu-massal-total-fields">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginTop: 20,
        }}
      >
        <InputDateTime
          label="Bulan distribusi kelambu massal"
          boldLabel
          name="net_distribution_date"
          dateFormat="YYYY-MM"
          placeholder="YYYY-MM"
          value={formData.kelambu_massal_total.net_distribution_date}
          onChange={(e) => {
            const formattedDate = moment(e)
              .startOf("month")
              .format("YYYY-MM-DD");

            const year = moment(e).format("YYYY");
            const month = moment(e).format("MM");

            handleInputChange(
              {
                target: {
                  name: "net_distribution_date",
                  value: formattedDate,
                },
              },
              "kelambu_massal_total"
            );

            fetchRemainingKelambuDesk(
              formData.urbanvillage_id,
              formattedDate?.split("-")[1],
              year,
              "Kelambu Massal Total"
            );

            // Panggil fetchPrevKelambuMassalDiterimaDesa berdasarkan bulan dan tahun yang dipilih
            if (fetchPrevKelambuMassalDiterimaDesa) {
              fetchPrevKelambuMassalDiterimaDesa(
                `${year}-${month}`,
                "Kelambu Massal Total"
              );
            }

            if(fetchTotalKelambuDesk){
              fetchTotalKelambuDesk(formData.urbanvillage_id, year);
            }
          }}
          isRequired={true}
        />

        <InputField
          label="Jumlah penduduk sasaran"
          type="number"
          placeholder="Masukkan Jumlah penduduk sasaran"
          name="total_population_target"
          onChange={(e) => handleInputChange(e, "kelambu_massal_total")}
          value={formData.kelambu_massal_total.total_population_target}
          disabled={true}
          isRequired={true}
        />

        <InputField
          label="Jumlah kelambu massal hasil desk"
          type="number"
          placeholder="Auto"
          name="total_mosquito_desk"
          onChange={(e) => handleInputChange(e, "kelambu_massal_total")}
          value={formData.kelambu_massal_total.total_mosquito_desk}
          disabled={true}
        />

        <InputField
          label="Jumlah kelambu massal yang diterima desa"
          type="number"
          placeholder="0"
          name="total_mosquito_nets_received"
          onChange={(e) => handleInputChange(e, "kelambu_massal_total")}
          value={formData?.kelambu_massal_total?.total_mosquito_nets_received}
          isRequired={true}
          disabled={disabledNetReceived || inputDisabled}
        />

        <div>
          <InputField
            label="Jumlah kelambu massal terdistribusi kepada ibu hamil"
            type="number"
            placeholder="Masukkan Jumlah kelambu terdistribusi kepada ibu hamil"
            name="total_nets_distributed_to_pregnant_women"
            onChange={clampAndForward}
            value={
              formData.kelambu_massal_total
                .total_nets_distributed_to_pregnant_women
            }
            isRequired={true}
            disabled={inputDisabled}
          />
          {fieldErrors.total_nets_distributed_to_pregnant_women ? (
            <div style={{ color: "#d9534f", fontSize: 12, marginTop: 6 }}>
              {fieldErrors.total_nets_distributed_to_pregnant_women}
            </div>
          ) : null}
        </div>

        <InputSelect
          label="Apakah akan didistribusikan sampai tingkat dusun?"
          name="is_dusun_level_distribution"
          placeholder="--PILIH--"
          data={YesAndNoOptions}
          onChange={(selected) => {
            const yes = selected?.value === "Ya";
            setFormData((prev) => ({
              ...prev,
              kelambu_massal_total: {
                ...prev.kelambu_massal_total,
                is_dusun_level_distribution: selected?.value || "",
                ...(yes
                  ? { total_net_distribution: totalDusun }
                  : {
                      total_sub_villages: "",
                      sub_villages_data: [],
                    }),
              },
            }));
            setRowErrors({});
          }}
          value={memoizedValues.kelambuMassalTotal.isDusunLevel}
          isDisabled={inputDisabled}
        />

        <div>
          <InputField
            label="Jumlah kelambu massal terdistribusi"
            type="number"
            placeholder={isDusun ? "Otomatis dari total dusun" : "0"}
            name="total_net_distribution"
            onChange={clampAndForward}
            value={formData.kelambu_massal_total.total_net_distribution}
            isRequired={true}
            disabled={isDusun || inputDisabled}
          />
          {!isDusun && fieldErrors.total_net_distribution ? (
            <div style={{ color: "#d9534f", fontSize: 12, marginTop: 6 }}>
              {fieldErrors.total_net_distribution}
            </div>
          ) : null}
        </div>

        {isDusun && (
          <InputField
            label="Jumlah dusun"
            type="number"
            placeholder="0"
            name="total_sub_villages"
            onChange={(e) => {
              setRowErrors({});
              handleInputChange(e, "kelambu_massal_total");
            }}
            value={formData.kelambu_massal_total.total_sub_villages}
            isRequired={true}
          />
        )}
      </div>

      {isDusun &&
        toInt(formData.kelambu_massal_total.total_sub_villages) > 0 && (
          <div style={{ marginTop: 20 }}>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th
                      style={{
                        minWidth: 200,
                        backgroundColor: "#f8f9fa",
                        textAlign: "center",
                      }}
                    >
                      Nama dusun
                    </th>
                    <th
                      style={{
                        minWidth: 200,
                        backgroundColor: "#f8f9fa",
                        textAlign: "center",
                      }}
                    >
                      Jumlah penduduk dusun
                    </th>
                    <th
                      style={{
                        minWidth: 200,
                        backgroundColor: "#f8f9fa",
                        textAlign: "center",
                      }}
                    >
                      Jumlah kelambu terdistribusi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({
                    length:
                      parseInt(
                        formData.kelambu_massal_total.total_sub_villages,
                        10
                      ) || 0,
                  }).map((_, index) => {
                    const subVillageData = formData.kelambu_massal_total
                      .sub_villages_data[index] || {
                      village_name: "",
                      population: "",
                      nets_distributed: "",
                    };

                    return (
                      <tr key={index}>
                        <td style={{ padding: "10px" }}>
                          <InputField
                            placeholder={`Nama dusun ${index + 1}`}
                            name="village_name"
                            disabled={inputDisabled}
                            value={subVillageData.village_name}
                            onChange={(e) => {
                              const updatedSubVillages = [
                                ...(formData.kelambu_massal_total
                                  .sub_villages_data || []),
                              ];
                              updatedSubVillages[index] = {
                                ...updatedSubVillages[index],
                                village_name: e.target.value,
                              };
                              setFormData((prev) => ({
                                ...prev,
                                kelambu_massal_total: {
                                  ...prev.kelambu_massal_total,
                                  sub_villages_data: updatedSubVillages,
                                },
                              }));
                            }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <InputField
                            type="number"
                            placeholder="0"
                            name="population"
                            disabled={inputDisabled}
                            value={subVillageData.population}
                            onChange={(e) => {
                              const updatedSubVillages = [
                                ...(formData.kelambu_massal_total
                                  .sub_villages_data || []),
                              ];
                              updatedSubVillages[index] = {
                                ...updatedSubVillages[index],
                                population: e.target.value,
                              };
                              setFormData((prev) => ({
                                ...prev,
                                kelambu_massal_total: {
                                  ...prev.kelambu_massal_total,
                                  sub_villages_data: updatedSubVillages,
                                },
                              }));
                            }}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>
                          <InputField
                            type="number"
                            placeholder="0"
                            name="nets_distributed"
                            value={subVillageData.nets_distributed}
                            disabled={inputDisabled}
                            onChange={(e) =>
                              handleChangeNetsDistributed(e, index)
                            }
                          />
                          {rowErrors[index] ? (
                            <div
                              style={{
                                color: "#d9534f",
                                fontSize: 12,
                                marginTop: 6,
                              }}
                            >
                              {rowErrors[index]}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 p-3 bg-light rounded">
              <div className="row">
                <div className="col-md-4">
                  <small className="text-muted">
                    Total penduduk semua dusun:
                  </small>
                  <div className="font-weight-bold">
                    {(formData.kelambu_massal_total.sub_villages_data || [])
                      .reduce((sum, v) => sum + toInt(v && v.population), 0)
                      .toLocaleString()}{" "}
                    orang
                  </div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">
                    Total kelambu terdistribusi:
                  </small>
                  <div className="font-weight-bold">
                    {totalDusun.toLocaleString()} kelambu
                  </div>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Sisa Kuota:</small>
                  <div
                    className={`font-weight-bold ${
                      totalDusun > totalReceived ? "text-danger" : ""
                    }`}
                  >
                    {Math.max(
                      remainingKelambu - totalDusun,
                      0
                    ).toLocaleString()}{" "}
                    kelambu
                  </div>
                </div>
              </div>

              {summaryError && (
                <div
                  className="mt-2"
                  style={{ color: "#d9534f", fontSize: 13 }}
                >
                  {summaryError}
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default KelambuMassalTotalFields;
